import React, { useState, useEffect } from 'react';
import { Maximize2, Table as TableIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { datasetApi } from '../../../api/datasetApi';
import SchemaTab from './SchemaTab';
import SampleTab from './SampleTab';
import InfoTab from './InfoTab';

const DatasetExplorer = ({ dataset, onMaximize }) => {
  const [activeTab, setActiveTab] = useState('schema');
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dataset) {
      setMetadata(null);
      return;
    }

    const fetchMetadata = async () => {
      setIsLoading(true);
      try {
        const data = await datasetApi.getDatasetMetadata(dataset.id);
        setMetadata(data);
      } catch (err) {
        console.error('Failed to fetch dataset metadata:', err);
        setMetadata(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [dataset]);

  if (!dataset) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-3">
          <TableIcon size={24} className="text-gray-300 dark:text-gray-700" />
        </div>
        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-1">Select a dataset</h4>
        <p className="text-[10px] text-gray-400 dark:text-gray-600">Pick a source from the sidebar to preview</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-4 h-4 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 transition-colors">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={dataset.name}>
            {dataset.type === 'bigquery' && dataset.name.includes('.')
              ? dataset.name.split('.').pop()
              : dataset.name}
          </h3>
          <div className="flex items-center gap-1">
            {['schema', 'sample', 'info'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider transition-all rounded-md",
                  activeTab === tab
                    ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                )}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={onMaximize}
          className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-md transition-all"
          title="Full Screen View"
        >
          <Maximize2 size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-hidden p-4">
        {activeTab === 'schema' && (
          <SchemaTab
            schema={metadata?.schema}
            summary={metadata?.summary}
            spatialColumns={metadata?.spatial_columns}
            temporalColumns={metadata?.temporal_columns}
          />
        )}
        {activeTab === 'sample' && (
          <SampleTab samples={metadata?.samples} />
        )}
        {activeTab === 'info' && (
          <InfoTab dataset={{ ...dataset, ...metadata }} />
        )}
      </div>
    </div>
  );
};

export default DatasetExplorer;
